"use client";
import { motion } from "framer-motion";
import {
  Trophy,
  Users,
  Zap,
  ShieldCheck,
  Gamepad2,
  ArrowUpRight,
  Send,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";
import { BRAND, MEDIA } from "../../lib/brand";

const features = [
  { icon: Gamepad2, label: "Multiplayer word games" },
  { icon: Trophy, label: "Real-time leaderboards" },
  { icon: Users, label: "Telegram group engagement" },
  { icon: Zap, label: "Smart automation triggers" },
  { icon: ShieldCheck, label: "Fast & reliable performance" },
  { icon: CheckCircle2, label: "Zero-setup join flow" },
];

const stats = [
  { value: "115K+", label: "Active Users" },
  { value: "2.2M+", label: "Games Played" },
  { value: "7K+", label: "Telegram Groups" },
  { value: "99.98%", label: "Uptime" },
];

export default function FeaturedBot() {
  return (
    <section id="featured" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#00E5FF] mb-5" data-testid="featured-label">
            Featured Bot
          </p>
          <h2
            className="font-display text-4xl md:text-5xl font-light tracking-tight"
            data-testid="featured-heading"
          >
            Meet{" "}
            <span className="gradient-text font-medium">Word Grid</span> — our
            flagship.
          </h2>
          <p className="mt-5 text-neutral-400 text-base md:text-lg">
            {BRAND.flagshipBot.description}
          </p>
        </motion.div>

        {/* Showcase card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="relative mt-12 rounded-3xl border-gradient glow-soft overflow-hidden"
          data-testid="featured-bot-card"
        >
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left content */}
            <div className="lg:col-span-7 p-8 md:p-12 lg:p-14">
              <div className="flex items-center gap-4">
                <div className="relative h-16 w-16 rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src={MEDIA.wordgrid_logo}
                    alt="Word Grid Logo"
                    className="h-full w-full object-cover"
                    data-testid="featured-bot-logo"
                  />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-medium">
                    Word Grid
                  </h3>
                  <div className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#229ED9]/15 border border-[#229ED9]/30 text-[#7CCBF1] text-[11px] font-medium">
                    <Send className="h-3 w-3" />
                    @WordGridRobot
                  </div>
                </div>
              </div>

              <p className="mt-7 text-neutral-300 text-base leading-relaxed max-w-xl">
                A multiplayer word-grid arena where Telegram groups battle in
                real-time, climb leaderboards and unlock daily challenges — all
                without leaving chat.
              </p>

              {/* Features grid */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                    data-testid={`featured-feature-${i}`}
                  >
                    <div className="h-8 w-8 rounded-lg grid place-items-center bg-gradient-to-br from-[#007AFF]/20 to-[#00E5FF]/10 border border-white/10">
                      <f.icon className="h-4 w-4 text-[#7CCBF1]" />
                    </div>
                    <span className="text-sm text-neutral-300">{f.label}</span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {stats.map((s, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-black/40 border border-white/10 p-4"
                    data-testid={`featured-stat-${i}`}
                  >
                    <p className="font-display text-2xl md:text-3xl font-medium">
                      {s.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button
                  asChild
                  className="bg-[#229ED9] hover:bg-[#1A8BC2] text-white rounded-full h-12 px-6 glow-blue"
                  data-testid="featured-cta-open"
                >
                  <a href={BRAND.flagshipBot.link} target="_blank" rel="noreferrer">
                    <Send className="h-4 w-4 mr-2" />
                    Open in Telegram
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full h-12 px-6 bg-transparent border-white/15 hover:bg-white/5 hover:border-white/30"
                  data-testid="featured-cta-learn"
                >
                  <a href="#services">
                    Learn more
                    <ArrowUpRight className="h-4 w-4 ml-2" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Right showcase */}
            <div className="relative lg:col-span-5 min-h-[360px] lg:min-h-full bg-gradient-to-br from-[#0A0A0A] to-[#050505] overflow-hidden border-t lg:border-t-0 lg:border-l border-white/10">
              <div className="absolute inset-0 dots-bg opacity-50" />
              <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-[#007AFF]/30 blur-3xl" />

              <motion.div
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative h-full p-8 md:p-10 flex items-center justify-center"
              >
                <div className="relative">
                  <div className="absolute -inset-6 rounded-full bg-[#00E5FF]/10 blur-3xl" />
                  <video
                    src={`${process.env.PUBLIC_URL}/media/wordgrid-live.mp4`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="relative h-56 md:h-72 w-auto rounded-2xl drop-shadow-[0_0_60px_rgba(0,229,255,0.3)]"
                    data-testid="featured-bot-hero-image"
                  />
                </div>
              </motion.div>

              {/* Live badge */}
              <div
                className="absolute top-5 right-5 inline-flex items-center gap-2 glass rounded-full px-3 py-1.5"
                data-testid="featured-live-badge"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                <span className="text-[11px] font-medium uppercase tracking-wider">
                  Live
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
